import { useState } from 'react'
import Icon from '../Icon'
import { listPlan, listPlants, listShows, listSpecies } from '../../lib/ppc'
import { VERSION, COMMIT, BUILD } from '../../lib/version'

/**
 * Export backup.
 *
 * Day 13 of the plan says "download inventory and sales backups and check they
 * open", and this is the button that makes that possible. It assembles what the
 * signed-in person can already read into one JSON file and hands it over.
 *
 * JSON, not CSV: the inventory is nested — plants point at species, shows point
 * at plants — and flattening it into a spreadsheet is how the relationships get
 * lost in exactly the situation the backup exists for. It carries the version
 * and commit that produced it, so a file found in six months can be read
 * against the code that wrote it.
 *
 * This is a COPY, not a restore path. It never claims to be one.
 */
export default function ExportBackup() {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function run() {
    setBusy(true)
    setError(null)
    try {
      const [plants, species, shows, plan] = await Promise.all([
        listPlants(), listSpecies(), listShows(), listPlan(),
      ])
      const backup = {
        app: 'Paradise Property Care',
        exportedAt: new Date().toISOString(),
        version: VERSION, build: BUILD, commit: COMMIT,
        counts: { plants: plants.length, species: species.length, shows: shows.length, planTasks: plan.length },
        plants, species, shows, plan,
      }
      const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      // Dated, so successive backups sort and do not overwrite each other.
      a.download = `ppc-backup-${new Date().toISOString().slice(0, 10)}.json`
      document.body.appendChild(a)
      a.click()
      a.remove()
      // Revoked on the next tick: revoking immediately cancels the download in
      // some browsers before it has started reading the blob.
      setTimeout(() => URL.revokeObjectURL(url), 0)
    } catch (problem) {
      setError(problem instanceof Error ? problem.message : 'The backup could not be built.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <button type="button" onClick={() => void run()} disabled={busy} title="Download a copy of the inventory">
        <Icon name="download" size={15} /> {busy ? 'Building…' : 'Export backup'}
      </button>
      {error && <span className="error small">{error}</span>}
    </>
  )
}
